import { useEffect, useId, useState } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Input,
  Label,
  SelectDropdown,
  Textarea,
  Tooltip,
  type SelectDropdownOption,
} from "@coval/ui"
import { Eye, EyeOff } from "lucide-react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import {
  DEEPGRAM_LISTEN_MODEL_SUGGESTIONS,
  DEEPGRAM_SPEAK_MODEL_SUGGESTIONS,
  DEEPGRAM_THINK_PROVIDERS,
  getDeepgramGuidedSettings,
  updateDeepgramGuidedSetting,
  type DeepgramGuidedSetting,
} from "@/utils/simulation/deepgramRealtimeSettings"
import {
  getJsonArrayError,
  getJsonObjectError,
  getTimeoutSecondsError,
  parseTimeoutSecondsInput,
} from "@/utils/simulation/realtimeFieldValidation"

interface DeepgramRealtimeFieldsProps {
  apiKey: string
  prompt?: string
  functions?: string
  settings?: string
  timeoutSeconds?: number
  requiredFieldKeys?: ReadonlySet<string>
  disabled?: boolean
  className?: string
  hideAdvancedSettings?: boolean
  hidePlaceholders?: boolean
  onApiKeyChange: (value: string) => void
  onPromptChange: (value: string) => void
  onFunctionsChange: (value: string) => void
  onSettingsChange: (value: string) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

interface DeepgramRealtimeAdvancedFieldsProps {
  functions?: string
  settings?: string
  timeoutSeconds?: number
  requiredFieldKeys?: ReadonlySet<string>
  disabled?: boolean
  className?: string
  hidePlaceholders?: boolean
  hideTimeout?: boolean
  showInlineHelp?: boolean
  onFunctionsChange: (value: string) => void
  onSettingsChange: (value: string) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

const CUSTOM_MODEL_VALUE = "__custom_model__"

interface GuidedModelSelectProps {
  label: string
  value: string
  suggestions: readonly string[]
  helpText: string
  disabled: boolean
  className: string
  onChange: (value: string) => void
  hidePlaceholder?: boolean
}

const GuidedModelSelect: React.FC<GuidedModelSelectProps> = ({
  label,
  value,
  suggestions,
  helpText,
  disabled,
  className,
  onChange,
  hidePlaceholder = false,
}) => {
  const labelId = useId()
  const customModelId = useId()
  const usesSuggestedModel = suggestions.includes(value)
  const [usesCustomModel, setUsesCustomModel] = useState(!usesSuggestedModel)

  useEffect(() => {
    setUsesCustomModel(!usesSuggestedModel)
  }, [usesSuggestedModel, value])

  const options: SelectDropdownOption[] = [
    ...suggestions.map((model) => ({ value: model, label: model })),
    {
      value: CUSTOM_MODEL_VALUE,
      label: "Custom Model ID…",
      description: "Enter a current model ID that is not listed above.",
    },
  ]

  return (
    <div className="space-y-2">
      <FieldLabel
        id={labelId}
        tooltip={
          <>
            {helpText} You can enter any current Deepgram model ID, including
            one not listed here.
          </>
        }
      >
        {label}
      </FieldLabel>
      <SelectDropdown
        aria-labelledby={labelId}
        options={options}
        value={
          usesCustomModel || !usesSuggestedModel ? CUSTOM_MODEL_VALUE : value
        }
        onValueChange={(selectedValue) => {
          if (selectedValue === CUSTOM_MODEL_VALUE) {
            setUsesCustomModel(true)
            return
          }
          setUsesCustomModel(false)
          onChange(selectedValue)
        }}
        searchable
        searchPlaceholder={`Search ${label.toLowerCase()}s`}
        disabled={disabled}
        className={className}
      />
      {(usesCustomModel || !usesSuggestedModel) && (
        <div className="space-y-2">
          <Label htmlFor={customModelId}>Custom Model ID</Label>
          <Input
            id={customModelId}
            value={usesSuggestedModel ? "" : value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={
              hidePlaceholder
                ? `Enter a ${label.toLowerCase()}`
                : "Enter a model ID"
            }
            disabled={disabled}
            className={className}
          />
        </div>
      )}
    </div>
  )
}

/** Fields for the hosted Deepgram Voice Agent Settings payload. */
export const DeepgramRealtimeFields: React.FC<DeepgramRealtimeFieldsProps> = ({
  apiKey,
  prompt,
  functions,
  settings,
  timeoutSeconds,
  requiredFieldKeys = new Set(),
  onApiKeyChange,
  onPromptChange,
  onFunctionsChange,
  onSettingsChange,
  onTimeoutSecondsChange,
  disabled,
  className = "",
  hideAdvancedSettings = false,
  hidePlaceholders = false,
}) => {
  const apiKeyId = useId()
  const thinkProviderLabelId = useId()
  const greetingId = useId()
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)
  const guidedSettings = getDeepgramGuidedSettings(settings)
  const selectedThinkProvider = DEEPGRAM_THINK_PROVIDERS.find(
    (provider) => provider.value === guidedSettings.thinkProvider,
  )
  const guidedFieldsDisabled = disabled || !guidedSettings.editable
  const isRequired = (key: string) => requiredFieldKeys.has(key)
  const updateGuidedSetting = (field: DeepgramGuidedSetting, value: string) =>
    onSettingsChange(updateDeepgramGuidedSetting(settings, field, value))

  const promptField = (
    <div className="space-y-2">
      <FieldLabel tooltip="Sent as the Deepgram agent's system prompt. Leave blank to use the prompt in the Settings override.">
        Agent System Prompt
      </FieldLabel>
      <Textarea
        value={prompt || ""}
        onChange={(event) => onPromptChange(event.target.value)}
        className={`text-sm ${className}`}
        disabled={disabled}
        rows={4}
        aria-label="Agent System Prompt"
        placeholder={
          hidePlaceholders
            ? "Describe how your agent should respond"
            : undefined
        }
        required={isRequired("deepgram_realtime_prompt")}
      />
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <FieldLabel tooltip="Configure the common listen, think, and speak settings. Model fields accept any current Deepgram model ID.">
          Voice Pipeline
        </FieldLabel>
        {!guidedSettings.editable && (
          <p className="rounded border border-coval-warning-border bg-coval-warning-surface px-3 py-2 text-xs text-coval-warning-text">
            This agent uses invalid JSON, provider fallbacks, or a custom
            endpoint or voice provider. Keep editing it in Advanced Settings;
            guided fields are paused to avoid overwriting unsupported values.
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <GuidedModelSelect
            label="Speech Recognition Model"
            value={guidedSettings.listenModel}
            suggestions={DEEPGRAM_LISTEN_MODEL_SUGGESTIONS}
            helpText="Nova uses v1; Flux uses v2. Coval keeps the required version selector in sync."
            disabled={guidedFieldsDisabled}
            className={className}
            onChange={(value) => updateGuidedSetting("listenModel", value)}
            hidePlaceholder={hidePlaceholders}
          />
          <GuidedModelSelect
            label="Agent Voice Model"
            value={guidedSettings.speakModel}
            suggestions={DEEPGRAM_SPEAK_MODEL_SUGGESTIONS}
            helpText="Flux voices use v2; Aura voices use v1. Coval pairs the selected model with the right version."
            disabled={guidedFieldsDisabled}
            className={className}
            onChange={(value) => updateGuidedSetting("speakModel", value)}
            hidePlaceholder={hidePlaceholders}
          />
          <div className="space-y-2">
            <FieldLabel
              id={thinkProviderLabelId}
              tooltip="Selecting a managed provider also switches to a compatible model. Groq, Bedrock, and custom endpoints remain in Advanced Settings."
            >
              Language Model Provider
            </FieldLabel>
            <SelectDropdown
              aria-labelledby={thinkProviderLabelId}
              options={DEEPGRAM_THINK_PROVIDERS.map((provider) => ({
                value: provider.value,
                label: provider.label,
              }))}
              value={guidedSettings.thinkProvider}
              onValueChange={(value) => {
                if (value) {
                  updateGuidedSetting("thinkProvider", value)
                }
              }}
              disabled={guidedFieldsDisabled}
              className={className}
            />
          </div>
          <GuidedModelSelect
            label="Language Model"
            value={guidedSettings.thinkModel}
            suggestions={selectedThinkProvider?.models ?? []}
            helpText="Choose a current managed model or use a custom model ID. Full provider configuration remains editable below."
            disabled={guidedFieldsDisabled}
            className={className}
            onChange={(value) => updateGuidedSetting("thinkModel", value)}
            hidePlaceholder={hidePlaceholders}
          />
        </div>
        <div className="space-y-2">
          <FieldLabel
            htmlFor={greetingId}
            tooltip="Set a greeting when the agent should speak before the persona."
          >
            Agent Greeting
          </FieldLabel>
          <Input
            id={greetingId}
            value={guidedSettings.greeting}
            onChange={(event) =>
              updateGuidedSetting("greeting", event.target.value)
            }
            placeholder={
              hidePlaceholders
                ? "Enter an opening greeting"
                : "Thanks for calling! How can I help?"
            }
            disabled={guidedFieldsDisabled}
            className={className}
          />
        </div>
      </div>
      <div className="space-y-2 border-t border-coval-border pt-4">
        <Label htmlFor={apiKeyId}>Deepgram API Key</Label>
        <Input
          id={apiKeyId}
          type={isApiKeyVisible ? "text" : "password"}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore
          data-op-ignore
          data-bwignore="true"
          placeholder="Enter your Deepgram API key"
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          required
          endAdornment={
            <Tooltip
              content={isApiKeyVisible ? "Hide API key" : "Show API key"}
            >
              <button
                type="button"
                onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
              >
                {isApiKeyVisible ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </Tooltip>
          }
        />
      </div>
      {hideAdvancedSettings && promptField}
      {!hideAdvancedSettings && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <FieldLabel tooltip="Maximum simulation duration in seconds. Defaults to 900.">
              Timeout
            </FieldLabel>
            <Input
              type="number"
              placeholder={
                hidePlaceholders ? "Set a timeout in seconds" : "900"
              }
              min={1}
              max={1800}
              value={timeoutSeconds?.toString() || ""}
              onChange={(event) =>
                onTimeoutSecondsChange(
                  parseTimeoutSecondsInput(event.target.value),
                )
              }
              className={className}
              disabled={disabled}
              error={!!timeoutError}
              required={isRequired("simulation_timeout_seconds")}
            />
            {timeoutError && (
              <p className="text-xs text-coval-danger-text">{timeoutError}</p>
            )}
          </div>
        </div>
      )}
      {!hideAdvancedSettings && (
        <Accordion type="single" collapsible className="px-0">
          <AccordionItem value="deepgram-realtime-advanced">
            <AccordionTrigger>
              <div className="flex flex-col items-start gap-0.5 text-left">
                <span>Advanced Settings</span>
                <span className="text-xs text-coval-text-muted font-normal">
                  Tool calls, endpoints, audio, fallbacks, and full Settings
                  JSON
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <DeepgramRealtimeAdvancedFields
                functions={functions}
                settings={settings}
                timeoutSeconds={timeoutSeconds}
                requiredFieldKeys={requiredFieldKeys}
                disabled={disabled}
                className={className}
                onFunctionsChange={onFunctionsChange}
                onSettingsChange={onSettingsChange}
                onTimeoutSecondsChange={onTimeoutSecondsChange}
                hidePlaceholders={hidePlaceholders}
                hideTimeout
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {!hideAdvancedSettings && promptField}
    </div>
  )
}

export function DeepgramRealtimeAdvancedFields({
  functions,
  settings,
  timeoutSeconds,
  requiredFieldKeys = new Set(),
  disabled,
  className = "",
  onFunctionsChange,
  onSettingsChange,
  onTimeoutSecondsChange,
  hidePlaceholders = false,
  hideTimeout = false,
  showInlineHelp = true,
}: DeepgramRealtimeAdvancedFieldsProps) {
  const functionsHelpId = useId()
  const settingsHelpId = useId()
  const functionsError = getJsonArrayError(functions)
  const settingsError = getJsonObjectError(settings)
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)
  const isRequired = (key: string) => requiredFieldKeys.has(key)

  return (
    <div className="space-y-5 pt-2">
      {!hideTimeout && (
        <div className="space-y-2">
          <FieldLabel tooltip="Maximum simulation duration in seconds. Defaults to 900.">
            Timeout
          </FieldLabel>
          <Input
            type="number"
            placeholder={hidePlaceholders ? "Set a timeout in seconds" : "900"}
            min={1}
            max={1800}
            value={timeoutSeconds?.toString() || ""}
            onChange={(event) =>
              onTimeoutSecondsChange(
                parseTimeoutSecondsInput(event.target.value),
              )
            }
            className={className}
            disabled={disabled}
            error={!!timeoutError}
            required={isRequired("simulation_timeout_seconds")}
          />
          {timeoutError && (
            <p className="text-xs text-coval-danger-text">{timeoutError}</p>
          )}
        </div>
      )}
      <div className="space-y-2">
        <FieldLabel
          tooltip={
            !showInlineHelp
              ? "Each tool needs a name and may include description, parameters, and endpoint. Deepgram runs tools with endpoints; without one, Coval records the call and simulates success without running customer code."
              : undefined
          }
        >
          Agent Tool Definitions
        </FieldLabel>
        <JsonCodeEditor
          hideGutters
          copyable
          placeholder={
            hidePlaceholders
              ? undefined
              : '[{"name": "get_weather", "description": "Get the weather for a location", "parameters": {"type": "object", "properties": {"location": {"type": "string"}}, "required": ["location"]}}]'
          }
          value={functions || ""}
          onChange={onFunctionsChange}
          className={className}
          disabled={disabled}
          rows={5}
          ariaLabel="Agent Tool Definitions"
          ariaDescribedBy={showInlineHelp ? functionsHelpId : undefined}
          ariaRequired={isRequired("deepgram_realtime_functions")}
          error={functionsError}
        />
        {showInlineHelp && (
          <p id={functionsHelpId} className="text-xs text-coval-text-muted">
            Each item needs a name and may include description, parameters, and
            endpoint. With an endpoint, Deepgram runs the tool. Without one,
            Coval records the call and returns a simulated success response; it
            does not execute customer code.
          </p>
        )}
      </div>
      <div className="space-y-2">
        <FieldLabel
          tooltip={
            !showInlineHelp
              ? "Deep-merged over Coval defaults. Configure provider endpoints, fallbacks, turn-taking, keyterms, audio sample rates, third-party voices, and newer Deepgram fields here. The prompt and tool definitions above take precedence."
              : undefined
          }
        >
          Full Deepgram Settings
        </FieldLabel>
        <JsonCodeEditor
          hideGutters
          copyable
          placeholder={
            hidePlaceholders
              ? undefined
              : '{"agent": {"listen": {"provider": {"type": "deepgram", "model": "nova-3"}}, "speak": {"provider": {"type": "deepgram", "version": "v2", "model": "flux-kit-en"}}, "greeting": "Thanks for calling!"}}'
          }
          value={settings || ""}
          onChange={onSettingsChange}
          className={className}
          disabled={disabled}
          rows={8}
          ariaLabel="Full Deepgram Settings JSON"
          ariaDescribedBy={showInlineHelp ? settingsHelpId : undefined}
          ariaRequired={isRequired("deepgram_realtime_settings")}
          error={settingsError}
        />
        {showInlineHelp && (
          <p id={settingsHelpId} className="text-xs text-coval-text-muted">
            Deep-merged over Coval defaults. Use this for provider endpoints,
            fallbacks, turn-taking, keyterms, audio sample rates, third-party
            voices, and any newer Deepgram field. The system prompt and tool
            definitions above take precedence over duplicate values here.
          </p>
        )}
      </div>
    </div>
  )
}
